import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from, firstValueFrom, Subscription } from 'rxjs';
import { switchMap, tap, throttleTime, map, filter } from 'rxjs/operators';
import * as AuthActions from './auth.actions';

import { AuthService } from '../../firebase/auth.service';

import { Action, Store } from '@ngrx/store';
import { AuthStoreDispatcher } from './auth.dispatcher';
import { SmartAudioService } from 'src/app/shared/services/smart-audio/smart-audio.service';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { ClubService } from '../../firebase/club.service';
import { GameThemeSettingsDispatcher } from '../gameThemeSettings/gameThemeSettings.dispatcher';
import { FirebaseReportingService } from '../../firebase/firebase-reporting.service';
import { AppState } from '../app.state';
import { NavigationExtras, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { GameStoreDispatcher } from '../game/game.dispatcher';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { NotificationService as FirebaseNotificationService } from '../../firebase/notification.service';
import { DynamicLinkService } from '../../firebase/dynamic-link.service';
import { ClubKickService } from '../../firebase/club-kick.service';
import { ApiService } from '../../backend/api.service';
import { IUserAccessResponse } from '@soposopo/sopo-models';
import { ProfileStateDispatcher } from '../profile/profile.dispatcher';
import { FeatureService } from '../../firebase/feature.service';
import { environment } from 'src/environments/environment';
import { ZendeskService } from 'src/app/shared/services/zendesk/zendesk.service';
import { CoinflowService } from 'src/app/shared/services/coinflow/coinflow.service';
import { CoinflowWithdrawService } from 'src/app/shared/services/coinflow/coinflow-withdraw.service';
import { SessionService } from '../../firebase/session.service';
import { FireSubService } from '../../firebase/fireSub.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { DebugModeService } from '../../firebase/debug-mode.service';
import { SopoCheatService } from 'src/app/sopo-cheat.service';

@Injectable()
export class AuthEffects {
    
    authenticatedAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.authenticatedAction),
        tap(async action => {
            this.firebaseReporting.setUserId(action.userData.uid);

            //notify BE to save the ip adress
            this.apiService.notifyBEOnUserAccess(action.userData.uid).catch((error) => console.log(error));

            this.coinflowService.initializeCoinflow(action.userData.uid);
            this.zendeskService.authentication(true);

            this.notificationService.notificationSubscribe(action.userData.uid)
                .catch((error) => console.error('Notification subscribe failed:', error));

            // Create or resume session (don't block navigation)
            this.initSession(action.userData.uid);

            // Check debug mode status
            this.debugModeService.checkDebugStatus();

            this.apiService.getNewUserAccess(action.userData.uid)
            .then((response: IUserAccessResponse) => {
                this.profileDispatcher.updateNextDailyAndNextStreak(response);
            }).catch((error) => {
                console.log(error);
            });

            if ((this.router.url.includes('/login') || this.router.url.includes('/password-change')) && !action.userData.isAnonymous) {
                /** TODO: Find out why zone is required for iOS */
                this.zone.run(async () => {
                    const parameters = this.dynamicLinkService.extractUrlParameters(this.router.url);

                    if (parameters['clubId'] && parameters['shortId']) {
                        const navigationExtras: NavigationExtras = { queryParams: { clubId: parameters['clubId'], shortId: parameters['shortId']} }
                        return await this.navCtrl.navigateRoot('lobby', navigationExtras);
                    }

                    if (parameters['inviteCode']) {
                        return await this.navCtrl.navigateRoot('lobby');
                    }

                    const queryParams = await firstValueFrom(this.router.routerState.root.queryParams);
                    await this.navCtrl.navigateRoot('lobby', { queryParams, state: { fromLogin: true }, queryParamsHandling: 'preserve' });
                });
            }
            this.clubKickService.init();
        }),
    ), {dispatch: false});

    
    notAuthenticatedAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.notAuthenticatedAction),
        tap(action => {
            if (window.name?.startsWith('gameTab-') && localStorage.getItem('startedLoginAsGuest') !== 'true') {
                window.close();
                return;
            }
            if(localStorage.getItem('hasWeakPassword')) localStorage.removeItem('hasWeakPassword');
            this.notificationService.notificationUnsubscribe();
            if (action.isAuthenticatedPreviously) {
                /** TODO: Find out why zone is required for iOS */
                this.zone.run(async () => {
                    if (!this.router.url.includes('/password-change')) await this.navCtrl.navigateRoot('login', { queryParamsHandling: 'preserve' });
                });
            }
        }),
    ), {dispatch: false});

    
    loginSuccess$: Observable<void | Action> = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginSuccessAction),
        throttleTime(5000),
        tap(() => {
            this.audioService.playSounds('join');
        }),
    ));

    
    loginWithEmailAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginWithEmailAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInWithEmailAndPassword(action.email, action.password)
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        localStorage.setItem('emailId', action.email);
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    loginAsGuestAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginAsGuestAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInAsGuest()
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        localStorage.removeItem('startedLoginAsGuest');
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        localStorage.removeItem('startedLoginAsGuest');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    loginWithFacebookAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginWithFacebookAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInWithFacebook(action.inviteCode)
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    loginWithGoogleAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginWithGoogleAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInWithGoogle(action.inviteCode)
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    loginWithAppleAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginWithAppleAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInWithApple(action.inviteCode)
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    loginWithWalletAction$= createEffect(() => this.actions$.pipe(
        ofType(AuthActions.loginWithWalletAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .signInWithWallet(action.inviteCode, action.wallet)
                    .then((credential) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.loginFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    logoutAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.logoutAction),
        tap(async action => {
            try {
                // End session before signing out
                await this.endCurrentSession();

                await this.authService.signOut(action.isDelete, action.logInOnOtherDevice);
                this.gameThemeSettings.resetSettings();
                this.clubService.stopWatchingClub();
                this.gameDispatcher.resetActions();
                this.clubKickService.destroySubscription();
                this.debugModeService.reset();
                this.sopoCheatService.reset();
                this.zendeskService.logoutZendesk();
                this.coinflowService.logoutClear();
                this.coinflowWithdrawService.clearIsWithdrawAllowed();
                if (!this.router.url.includes('/password-change')) {
                    let navExtras: NavigationExtras = {
                        queryParamsHandling: 'preserve'
                    };
                    if (action.inviteCode) navExtras.queryParams = {inviteCode: action.inviteCode};
                    this.navCtrl.navigateRoot('/login', navExtras);
                }
            } catch (error) {
                this.store.dispatch(AuthActions.authErrorAction({ error }));
            }
        }),
    ), {dispatch: false});

    
    resetPasswordAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.resetPasswordAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .sendPasswordResetEmail(action.email)
                    .then((action) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.resetPasswordSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.resetPasswordFailedAction({
                            error: error,
                        });
                    }),
            );
        }),
    ));

    
    signupAction$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.signupAction),
        switchMap((action) => {
            this.loadingCtrl.present('auth');
            return from(
                this.authService
                    .createUserWithEmailAndPassword(action.email, action.password, action.inviteCode)
                    .then((userCredential) => {
                        const email = userCredential.user.email ? userCredential.user.email : '';
                        localStorage.setItem('emailId', email);
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.signupSuccessAction();
                    })
                    .catch((error) => {
                        this.loadingCtrl.dismiss('auth');
                        return AuthActions.signupErrorAction(error);
                    }),
            );
        }),
    ));

    
    signUpSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.signupSuccessAction),
        map(() => {
            return AuthActions.loginSuccessAction();
        }),
    ));

    private sessionWatcherSubscription: Subscription | null = null;
    private currentSessionId: string | null = null;

    private async initSession(userId: string): Promise<void> {
        try {
            const deviceInfo = await this.sessionService.getDeviceInfo();
            const fcmToken = await this.firebaseNotificationService.getToken()
                .catch(e => {
                    if (e?.code === 'messaging/permission-blocked') {
                        console.debug('[FCM] Permission blocked, continuing without token');
                        return null;
                    }
                    throw e;
                }) ?? '';

            const sessionId = await this.sessionService.resumeOrCreateSession(userId, {
                fcmToken,
                appVersionNumber: deviceInfo.appVersionNumber,
                manufacturer: deviceInfo.manufacturer,
                deviceVersion: deviceInfo.deviceVersion,
            });

            this.currentSessionId = sessionId;
            this.startSessionWatcher(userId, sessionId);
        } catch (error) {
            console.error('Failed to init session:', error);
        }
    }

    private startSessionWatcher(userId: string, sessionId: string): void {
        if (this.sessionWatcherSubscription) {
            this.sessionWatcherSubscription.unsubscribe();
        }

        this.sessionService.resetIsLoggingOut();

        this.sessionWatcherSubscription = this.sessionService
            .watchSession(userId, sessionId)
            .pipe(filter(sessionData => sessionData !== undefined))
            .subscribe(sessionData => {
                if (this.sessionService.getIsLoggingOut()) return;

                if (sessionData.sessionEnd) {
                    const endReason = sessionData.endReason;
                    let message = 'Your session has ended';
                    if (endReason === 'new_session_same_device') {
                        message = 'You\'ve been logged out because a new session was started on this device';
                    } else if (endReason === 'admin') {
                        message = 'Your session was ended by an administrator';
                    }
                    this.toastService.toast(message);
                    this.sessionService.clearStoredSessionId();
                    this.authStore.logout();
                }
            });

        this.fireSubService.registerSubscription(this.sessionWatcherSubscription);
    }

    private async endCurrentSession(): Promise<void> {
        const sessionId = this.currentSessionId || this.sessionService.getStoredSessionId();
        if (!sessionId) return;

        try {
            const userId = await this.profileDispatcher.getMyID();
            await this.sessionService.endSession(userId, sessionId);
        } catch (error) {
            console.error('Failed to end session on logout:', error);
        }
        this.currentSessionId = null;
    }

    constructor(
        private authService: AuthService,
        private authStore: AuthStoreDispatcher,
        private gameDispatcher: GameStoreDispatcher,
        private actions$: Actions,
        private audioService: SmartAudioService,
        private loadingCtrl: SopoLoadingService,
        private clubService: ClubService,
        private store: Store<AppState>,
        private zone: NgZone,
        private navCtrl: NavController,
        private gameThemeSettings: GameThemeSettingsDispatcher,
        private firebaseReporting: FirebaseReportingService,
        private router: Router,
        private notificationService: NotificationService,
        private firebaseNotificationService: FirebaseNotificationService,
        private apiService: ApiService,
        private profileDispatcher: ProfileStateDispatcher,
        private dynamicLinkService: DynamicLinkService,
        private clubKickService: ClubKickService,
        private featureService: FeatureService,
        private zendeskService: ZendeskService,
        private coinflowService: CoinflowService,
        private coinflowWithdrawService: CoinflowWithdrawService,
        private sessionService: SessionService,
        private fireSubService: FireSubService,
        private toastService: ToastService,
        private debugModeService: DebugModeService,
        private sopoCheatService: SopoCheatService,
    ) {}
}
